/**
 * Typed hooks for the enrollment store
 *
 * These hooks provide type-safe access to the enrollment store's selectors and actions.
 * Use these hooks in frontend components to access user enrollment state.
 *
 * @example
 * ```tsx
 * const EnrolledHeading = () => {
 *   const { status, isEnrolled, credits } = useEnrollment();
 *
 *   if (status === 'guest') {
 *     return <h1>Join our rewards program</h1>;
 *   }
 *
 *   if (isEnrolled) {
 *     return <h1>You have {credits} points!</h1>;
 *   }
 *
 *   return <h1>Ready to join?</h1>;
 * };
 * ```
 */
import React from 'react';
import { useSelect, useDispatch } from '@wordpress/data';
import type {
	Contact,
	EnrollmentStatus,
	EnrollmentStoreConfig,
} from '../store/enrollment-store';
import { ENROLLMENT_STORE_NAME } from '../store/enrollment-store';

// ============================================================================
// Hook Return Types
// ============================================================================

/**
 * Complete enrollment state returned by useEnrollment hook
 */
export interface UseEnrollmentReturn {
	/** Current enrollment status */
	status: EnrollmentStatus;
	/** Contact data (null if not enrolled) */
	contact: Contact | null;
	/** User's current tier (null if not enrolled or no tier assigned) */
	tier: import('../store/enrollment-store').Tier | null;
	/** Whether user is enrolled */
	isEnrolled: boolean;
	/** Whether user is logged in (but may not be enrolled) */
	isLoggedIn: boolean;
	/** Whether user is a guest */
	isGuest: boolean;
	/** Whether data is currently loading */
	isLoading: boolean;
	/** Error message if fetch failed */
	error: string | null;
	/** User's credit balance */
	credits: number;
	/** Credits currency name */
	creditsCurrency: string;
	/** Claimed rewards list */
	claimedRewards: Array<{ earn_rule_id: string }>;
	/** Function to refetch enrollment data */
	refetch?: () => void;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to get complete enrollment state
 *
 * This hook fetches enrollment data from the REST API on first use (via resolvers)
 * and returns all enrollment-related state in a single object.
 *
 * @returns Complete enrollment state
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { status, isEnrolled, credits, isLoading, refetch } = useEnrollment();
 *
 *   if (isLoading) {
 *     return <div>Loading...</div>;
 *   }
 *
 *   if (isEnrolled) {
 *     return <div>You have {credits} points!</div>;
 *   }
 *
 *   return <div>Join our program!</div>;
 * };
 * ```
 */
/**
 * WordPress data store selector functions (bound to state)
 * These are returned by select() and already have state bound to them
 */
interface BoundEnrollmentSelectors {
	getEnrollmentStatus(): EnrollmentStatus;
	getContact(): Contact | null;
	getTier(): import('../store/enrollment-store').Tier | null;
	getClaimedRewards(): Array<{ earn_rule_id: string }>;
	isEnrolled(): boolean;
	isLoggedIn(): boolean;
	isGuest(): boolean;
	isLoading(): boolean;
	getError(): string | null;
	getCredits(): number;
	getCreditsCurrency(): string;
}

export function useEnrollment(): UseEnrollmentReturn {
	const data = useSelect((select) => {
		// When using select(), WordPress data returns bound selector functions
		// that don't need the state passed explicitly
		const {
			getEnrollmentStatus,
			getContact,
			getTier,
			isEnrolled,
			isLoggedIn,
			isGuest,
			isLoading,
			getError,
			getCredits,
			getCreditsCurrency,
			getClaimedRewards,
		} = select(ENROLLMENT_STORE_NAME) as BoundEnrollmentSelectors;

		return {
			status: getEnrollmentStatus(),
			contact: getContact(),
			tier: getTier(),
			isEnrolled: isEnrolled(),
			isLoggedIn: isLoggedIn(),
			isGuest: isGuest(),
			isLoading: isLoading(),
			error: getError(),
			credits: getCredits(),
			creditsCurrency: getCreditsCurrency(),
			claimedRewards: getClaimedRewards(),
		};
	}, []);

	// Get dispatch functions for refetch capability
	const { invalidateResolution } = useDispatch('core/data') as {
		invalidateResolution: (storeName: string, selectorName: string, args: unknown[]) => void;
	};

	// Provide a refetch function that invalidates the contact resolver
	const refetch = () => {
		invalidateResolution(ENROLLMENT_STORE_NAME, 'getContact', []);
	};

	return {
		...data,
		refetch,
	};
}


/**
 * Hook to get enrollment store actions
 *
 * Use this to manually update enrollment state (e.g., after joining program).
 *
 * @returns Enrollment store actions
 *
 * @example
 * ```tsx
 * const { setContact, setEnrollmentStatus } = useEnrollmentActions();
 *
 * const handleJoinProgram = async () => {
 *   const contact = await joinProgram();
 *   setContact(contact);
 *   setEnrollmentStatus('enrolled');
 * };
 * ```
 */
export function useEnrollmentActions(): EnrollmentStoreConfig['actions'] {
	return useDispatch(ENROLLMENT_STORE_NAME) as EnrollmentStoreConfig['actions'];
}

/**
 * Hook to track whether enrollment data has been received
 *
 * This consolidates the common pattern of tracking whether we've received
 * enrollment data to prevent showing skeleton loaders unnecessarily.
 *
 * @returns Boolean indicating if enrollment data has been fully loaded
 *
 * @example
 * ```tsx
 * const MyBlockView = ({ initialContent }) => {
 *   const { credits, creditsCurrency } = useEnrollment();
 *   const hasData = useHasEnrollmentData();
 *
 *   if (!hasData) {
 *     return <Skeleton />;
 *   }
 *
 *   return <div>{credits} {creditsCurrency}</div>;
 * };
 * ```
 */
export function useHasEnrollmentData(): boolean {
	const { isLoading, status, credits } = useEnrollment();
	const [hasData, setHasData] = React.useState(false);

	React.useEffect(() => {
		// Consider data ready when:
		// 1. Not loading anymore
		// 2. Status is enrolled
		// 3. Credits value is defined (could be 0)
		if (!isLoading && status === 'enrolled' && credits !== undefined) {
			setHasData(true);
		}
	}, [isLoading, status, credits]);

	return hasData;
}
